import _orderBy from 'lodash/orderBy';

import { getLessonCategoryIdsFromApiLesson } from 'helpers/getLessonCategoryIdsFromApiLesson';

import { ensureLayerFormProperties } from './ensureLayerFormProperties';
import {
  normalizeFlowStartingPointsForForm,
  normalizePrototypeStartNodeForForm,
  normalizeReactionsForForm,
} from './normalizePrototypeMetaForForm';
import {
  getRawLayerOrder,
  normalizeLayerPropertiesInput,
} from './normalizeLayerPropertiesInput';

const _orderLayers = (layers: any) =>
  _orderBy(layers, [getRawLayerOrder], ['asc']);

const _formatLayer = (layer: any, layerOrder: number): any => {
  const checkProperties = layer.checkProperties || [];
  const properties = normalizeLayerPropertiesInput({
    properties: layer.properties,
    checkProperties,
  }).map((property) => {
    if (property.key !== 'reactions') {
      return property;
    }

    return {
      ...property,
      value: normalizeReactionsForForm(property.value),
    };
  });

  return {
    id: layer.id,
    name: layer.name,
    type: layer.type,
    properties: ensureLayerFormProperties({
      properties,
      checkProperties,
      layerType: layer.type,
      layerOrder,
    }),
    options: layer.options,
    children: _orderLayers(layer.children || []).map(
      (childLayer: any, childLayerOrder: number): any =>
        _formatLayer(childLayer, childLayerOrder),
    ),
  };
};

const _formatComponentsLayer = (layer: any) => {
  const properties = normalizeLayerPropertiesInput({
    properties: layer.properties,
  });

  return {
    id: layer.id,
    name: layer.name,
    type: layer.type,
    properties,
    children: (layer.children || []).map((childLayer: any) =>
      _formatComponentsLayer(childLayer),
    ),
  };
};

const _formatTask = (task: any) => ({
  id: task.id,
  name: task.name,
  text: task.text || [],
  preview: task.preview,
  flowStartingPoints: normalizeFlowStartingPointsForForm(task.flowStartingPoints),
  prototypeStartNode: normalizePrototypeStartNodeForForm(task.prototypeStartNode),
  layers: task.layers
    ? _orderLayers(task.layers).map((layer: any, layerOrder: number) =>
        _formatLayer(layer, layerOrder),
      )
    : [],
  // withTheory: task.theoryBlocks && !!task.theoryBlocks.length,
  withHints: task.hintsBlocks && !!task.hintsBlocks.length,
  withMaterials:
    task.materials &&
    !!task.materials.filter((material: any) => material.check).length,
  withFiles: task.files && task.files.length > 0,
  // theoryBlocks: task.theoryBlocks || [],
  checkStyles: task.checkStyles,
  hintsBlocks: task.hintsBlocks || [],
  materials: task.materials || [],
  files: task.files || [],
});

export const convertDataToForm = (data: any, readOnly: any) => {
  return {
    id: data.id,
    type: data.type,
    kind: data.kind,
    moduleId: data.moduleId,
    courseId: data.courseId,
    status: data.status,
    fileUrl: '',
    fileId: data.fileId,
    filePageId: data.filePageId,
    designType: data.designType || null,
    title: data.title || '',
    description: data.description || [],
    percentToComplete: data.percentToComplete,
    resultRefPreview: data.resultRefPreview,
    initialState: data.initialState
      ? {
          preview: data.initialState.preview,
          flowStartingPoints: normalizeFlowStartingPointsForForm(
            data.initialState.flowStartingPoints,
          ),
          prototypeStartNode: normalizePrototypeStartNodeForForm(
            data.initialState.prototypeStartNode,
          ),
          componentsLayer: data.initialState.componentsLayer
            ? _formatComponentsLayer(data.initialState.componentsLayer)
            : null,
          layers: _orderLayers(data.initialState.layers).map(
            (layer: any, layerOrder: number) => _formatLayer(layer, layerOrder),
          ),
        }
      : null,
    tasks: data.tasks ? data.tasks.map((task: any) => _formatTask(task)) : [],
    free: data.free,
    withGoodToKnow:
      data.goodToKnowBlocks && data.goodToKnowBlocks.length > 0 ? true : false,
    withCanvasTheory:
      data.canvasTheory && data.canvasTheory.length > 0 ? true : false,
    goodToKnowBlocks: data.goodToKnowBlocks || [],
    canvasTheory: data.canvasTheory || [],
    readOnly,
    goals: data.goals || [],
    categories: getLessonCategoryIdsFromApiLesson(data),
  };
};
